import { Box, IconButton, Stack, SxProps, TableCell, TableRow, Theme, Typography, useTheme } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { ReadTaskSummaryData } from '@common/config/api/client'
import { TableScrollSize } from '@features/reader_summary/pages/summary.types'
import { DescriptionOutlined } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'

function TimepointCell({
  i,
  tableScrollSize,
  stickyStyles,
  theme,
}: {
  i: number
  tableScrollSize: TableScrollSize
  stickyStyles: SxProps<Theme>
  theme: Theme
}) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [cellHeight, setCellHeight] = useState<number>(0)

  useEffect(() => {
    if (cellRef.current) {
      const { height } = cellRef.current.getBoundingClientRect()
      setCellHeight(height)
    }
  }, [])

  return (
    <TableCell
      key={`timepoint-${i}`}
      ref={cellRef}
      sx={{
        width: tableScrollSize.columnWidth,
        minWidth: tableScrollSize.columnWidth,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        padding: 0,
        ...stickyStyles,
      }}
    >
      <Stack direction="row" width={tableScrollSize.columnWidth} maxWidth={tableScrollSize.columnWidth}>
        <Box sx={{ height: cellHeight, width: '50%' }} />
        <Box
          sx={{
            height: cellHeight,
            width: '50%',
            borderLeft: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Stack>
    </TableCell>
  )
}

export default function ErcfTableRow({
  expanded,
  timepoints,
  tableScrollSize,
  setExpanded,
}: {
  expanded: boolean
  timepoints: ReadTaskSummaryData['timepoints']
  tableScrollSize: TableScrollSize
  setExpanded: (expanded: boolean) => void
}) {
  const theme = useTheme()

  return (
    <TableRow
      data-testid="subject-table-row"
      hover
      sx={{
        '&:hover': {
          '& > td': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        },
        bgcolor: 'background.paper',
      }}
    >
      <TableCell
        data-testid="pseudo-id-cell"
        sx={{
          width: tableScrollSize.columnWidth,
          minWidth: tableScrollSize.columnWidth,
          position: 'sticky',
          left: 0,
          background: 'inherit',
          zIndex: 3,
          paddingLeft: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            color: 'text.primary',
          }}
        >
          <DescriptionOutlined />
          <Typography
            variant="button"
            sx={{
              color: 'text.primary',
            }}
          >
            eCRF
          </Typography>
        </Stack>
      </TableCell>
      {Array.from({ length: tableScrollSize.emptyCellsCount }).map((_, i) => (
        <TableCell
          key={`empty-${i}`}
          sx={{
            width: tableScrollSize.columnWidth,
            minWidth: tableScrollSize.columnWidth,

            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        />
      ))}
      {timepoints.map((_, i) => {
        let stickyStyles: SxProps<Theme> = {}

        if (i === 0) {
          stickyStyles = {
            position: 'sticky',
            left: tableScrollSize.firstTimepointLeftOffset,
            background: 'inherit',
            zIndex: 2,
          }
        }

        if (i === timepoints.length - 1) {
          stickyStyles = {
            position: 'sticky',
            right: tableScrollSize.checkboxWidth,
            background: 'inherit',
            zIndex: 2,
            borderLeft: `1px solid ${theme.palette.primary.main}`,
          }
        }

        return (
          <TimepointCell
            key={`timepoint-${i}`}
            i={i}
            tableScrollSize={tableScrollSize}
            stickyStyles={stickyStyles}
            theme={theme}
          />
        )
      })}

      <TableCell
        padding="checkbox"
        data-testid="expand-cell"
        sx={{
          width: tableScrollSize.checkboxWidth,
          minWidth: tableScrollSize.checkboxWidth,
          position: 'sticky',
          right: 0,
          background: 'inherit',
          zIndex: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderLeft: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded(!expanded)
          }}
          data-testid="expand-button"
        >
          {expanded ? (
            <KeyboardArrowUpIcon data-testid="icon-expanded" />
          ) : (
            <KeyboardArrowDownIcon data-testid="icon-collapsed" />
          )}
        </IconButton>
      </TableCell>
    </TableRow>
  )
}
